import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '@/lib/api';

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  role: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface CreatedToken {
  token: string;
  name: string;
  prefix: string;
}

interface UseApiTokensReturn {
  tokens: ApiToken[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  fetchTokens: () => Promise<void>;
  createToken: (name: string) => Promise<CreatedToken>;
  revokeToken: (id: string) => Promise<void>;
}

export const useApiTokens = (): UseApiTokensReturn => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTokens = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/tokens');
      if (!response.ok) {
        throw new Error(`Failed to fetch tokens: ${response.status}`);
      }
      const data = await response.json();
      setTokens(data.tokens || []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const createToken = useCallback(
    async (name: string): Promise<CreatedToken> => {
      setCreating(true);
      setError(null);
      try {
        const response = await apiFetch('/tokens', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.error || `Failed to create token: ${response.status}`);
        }
        const created: CreatedToken = await response.json();
        await fetchTokens();
        return created;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [fetchTokens],
  );

  const revokeToken = useCallback(
    async (id: string): Promise<void> => {
      setError(null);
      try {
        const response = await apiFetch(`/tokens/${id}`, { method: 'DELETE' });
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.error || `Failed to revoke token: ${response.status}`);
        }
        await fetchTokens();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        throw err;
      }
    },
    [fetchTokens],
  );

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  return {
    tokens,
    loading,
    error,
    creating,
    fetchTokens,
    createToken,
    revokeToken,
  };
};
